import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import { m } from "#/paraglide/messages";
import { toResult } from "#/modules/util.auth/api-errors";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { getEnvironmentInfo } from "#/modules/util.environment/env.server";
import {
  getUserInfo,
  login as loginApi,
  verify2FALogin as verify2FALoginApi,
} from "#/modules/util.auth/api.auth.server";
import { completeLoginAndRedirect, loginCallback } from "#/modules/util.auth/authenticate.server";
import { sanitizeRedirect } from "#/modules/util.auth/sanitize-redirect";
import { getAuthSession, type UserSession } from "#/modules/util.auth/session.auth.server";
import { genericFormError, translateApiError } from "#/modules/util.errors/translate";
import { setSentryUser } from "#/modules/util.instrumentation/sentry";
import { loginSchema } from "#/modules/generic.login/schema.login";
import { forgotPasswordSchema } from "#/modules/generic.login/schema.forgot-password";

/**
 * If there is a logged-in session, throw the generic post-login redirect (loginCallback);
 * otherwise no-op. Call from the login/signup/etc. routes' beforeLoad so an already
 * authenticated user is sent to their destination instead of seeing the auth form.
 */
export const redirectIfAuthenticated = createServerFn({ method: "GET" }).handler(async () => {
  const session = await getAuthSession();
  if (session.data.user) {
    await loginCallback(); // throws a redirect
  }
  return null;
});

/** Login-page loader: dev autofill creds (the return URL is stored at submit time in loginFn). */
export const prepareLogin = createServerFn({ method: "GET" })
  .validator((data: { email?: string }) => data)
  .handler(async ({ data }) => {
    const { isDevelopment } = getEnvironmentInfo();
    return {
      email: data.email || (isDevelopment ? process.env.EMAIL || "" : ""),
      password: isDevelopment ? process.env.PASSWORD || "" : "",
    };
  });

/**
 * POST /auth/login. On success completes the login (session + redirect) via
 * completeLoginAndRedirect; when 2FA is required, stashes the temporary tokens and
 * returns { requiresTwoFactor }.
 */
export const loginFn = createServerFn({ method: "POST" })
  .validator(loginSchema.extend({ referer: z.string().optional() }))
  .handler(
    async ({ data }): Promise<{ formError: string } | { requiresTwoFactor: true } | undefined> => {
      setSentryUser(data.email);
      const result = await loginApi(data.email, data.password);
      if (Result.isError(result)) {
        return { formError: translateApiError(result.error) };
      }

      const tokens = result.value;

      if (tokens.requiresTwoFactor) {
        // Keep the temporary token so the 2FA step can complete the login, and stash the
        // post-login return URL now — the 2FA step has no referer of its own.
        const authRedirect = sanitizeRedirect(data.referer);
        const session = await getAuthSession();
        const sessionUpdate: Partial<UserSession> = { tokens };
        if (authRedirect) sessionUpdate.authRedirect = authRedirect;
        await session.update(sessionUpdate);
        return { requiresTwoFactor: true };
      }

      const userResult = await getUserInfo(tokens.accessToken ?? "");
      if (Result.isError(userResult)) {
        return { formError: translateApiError(userResult.error) };
      }

      await completeLoginAndRedirect({ tokens, user: userResult.value, referer: data.referer });
      return undefined;
    },
  );

/** POST /auth/login/2fa — completes login using the temporary token from the session. */
export const verify2FALoginFn = createServerFn({ method: "POST" })
  .validator(z.object({ email: z.string().email(), code: z.string().min(6).max(6) }))
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    setSentryUser(data.email);
    const session = await getAuthSession();
    const temporaryToken = session.data.tokens?.temporaryToken;
    if (!temporaryToken) {
      // The password step's temporary token is gone (expired session, stale tab).
      return genericFormError("TwoFactorTokenMissing");
    }

    const result = await verify2FALoginApi(data.email, temporaryToken, data.code);
    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    const tokens = result.value;
    const userResult = await getUserInfo(tokens.accessToken ?? "");
    if (Result.isError(userResult)) {
      return { formError: translateApiError(userResult.error) };
    }

    // No referer here: the loginFn 2FA branch already stashed authRedirect, which
    // completeLogin leaves intact when no referer is passed.
    await completeLoginAndRedirect({ tokens, user: userResult.value });
    return undefined;
  });

/** POST /auth/logout — clears the backend session and the cookie. */
export const logoutFn = createServerFn({ method: "POST" }).handler(async () => {
  // Fire-and-forget: a failed backend logout must not block clearing the local session.
  toResult(await client.POST("/auth/logout"));
  const session = await getAuthSession();
  await session.clear();
});

/** POST /auth/forgot-password — always reports success (avoids account enumeration). */
export const forgotPasswordFn = createServerFn({ method: "POST" })
  .validator(forgotPasswordSchema)
  .handler(async ({ data }) => {
    // Errors deliberately ignored: the caller always sees success.
    toResult(await client.POST("/auth/forgot-password", { body: { email: data.email } }));
  });

/** POST /auth/reset-password — sets a new password using the emailed token. */
export const resetPasswordFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      email: z.string().email(),
      token: z.string().min(1),
      newPassword: z.string().min(8),
    }),
  )
  .handler(async ({ data }): Promise<{ formError: string } | undefined> => {
    setSentryUser(data.email);
    const result = toResult(
      await client.POST("/auth/reset-password", {
        body: { email: data.email, token: data.token, newPassword: data.newPassword },
      }),
    );
    if (Result.isError(result)) {
      return { formError: m["auth.api_errors.password_reset_failed"]() };
    }
    return undefined;
  });
